import { useQuery } from '@tanstack/react-query';

import { listLineFriends } from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { userStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import { toErrorMessage } from 'hooks/utils';
import type { TLineFriendListItem, TListLineFriendsRequest } from 'types';

type UseLineFriendsParams = Partial<TListLineFriendsRequest>;

type UseLineFriendsResult = {
  friends: TLineFriendListItem[];
  total: number;
  totalAll: number;
  currentPage: number;
  totalPage: number;
  loading: boolean;
  error: string | undefined;
};

export function useLineFriends(
  params: UseLineFriendsParams = {},
): UseLineFriendsResult {
  const { user } = useAuth();
  const currentPage = params.currentPage ?? 1;
  const pageSize = params.pageSize ?? 20;
  const filters = {
    search: params.search?.trim() || undefined,
    friendStatus: params.friendStatus || undefined,
    tagId: params.tagId || undefined,
    lastAccessFrom: params.lastAccessFrom || undefined,
    lastAccessTo: params.lastAccessTo || undefined,
    friendRegisteredFrom: params.friendRegisteredFrom || undefined,
    friendRegisteredTo: params.friendRegisteredTo || undefined,
    isTestUser: params.isTestUser || undefined,
    isUntagged: params.isUntagged || undefined,
    // 流入元（#403）。ここに載せないと queryKey も queryFn も素通りし、
    // 絞り込んでもリクエストすら飛ばない。
    adCodeIds: params.adCodeIds?.length ? params.adCodeIds : undefined,
    adCodeMode: params.adCodeIds?.length ? params.adCodeMode : undefined,
    hasNoAdCode: params.hasNoAdCode || undefined,
  };

  const query = useQuery({
    queryKey: [
      queryKeys.LINE_FRIENDS,
      user?.uid,
      currentPage,
      pageSize,
      filters,
    ],
    enabled: Boolean(user?.uid),
    queryFn: () =>
      listLineFriends({
        currentPage,
        pageSize,
        ...filters,
      }),
    retry: false,
  });

  return {
    friends: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    totalAll: query.data?.totalAll ?? query.data?.total ?? 0,
    currentPage: query.data?.currentPage ?? currentPage,
    totalPage: query.data?.totalPage ?? 0,
    loading: Boolean(user?.uid) && query.isPending,
    error: query.error
      ? toErrorMessage(query.error, userStrings.errors.loadFriendsFailed)
      : undefined,
  };
}
